import {
  type IBillData,
  type IOverviewJointBillService,
  type IRequestedSpBillData,
} from "@services/overview/types";
import { api } from "@/api";
import { type IBase64File } from "@/types";
import {
  type IAddRequestedServiceProviderData,
  type IAddServiceProviderRequest,
  type IAddServiceProviderResponse,
  type IEditServiceProviderDueDayRequest,
  type IEditServiceProviderRequest,
  type IRequestedServiceProvider,
  type IServiceProvider,
  type IServiceProviderPaymentInstructionCodeReponse,
  type IServiceProviderPaymentInstructionCodeRequest,
  type IServiceProviderRegistrationInfo,
  type IServiceProviderRegistrationInfoRequest,
} from "./types";

export const getServiceProviders = async (jointBillServiceId: number) =>
  (
    await api.get<IServiceProvider[]>("/service-providers", {
      headers: { jointBillServiceId },
    })
  ).data;

export const getServiceProviderRegistrationInfo = async (
  data: IServiceProviderRegistrationInfoRequest,
): Promise<IServiceProviderRegistrationInfo> =>
  (
    await api.get<{
      serviceProviderRegistrationInfo: IServiceProviderRegistrationInfo;
    }>(
      `/service-providers/${data.serviceProviderId}/joint-bill-services/registration-info`,
      {
        headers: { jointBillServiceId: data.jointBillServiceId },
      },
    )
  ).data?.serviceProviderRegistrationInfo;

export const addServiceProvider = (data: IAddServiceProviderRequest) =>
  api
    .post<IAddServiceProviderResponse>(`/joint-bill-service-agreements`, data)
    .then((resp) => resp.data);

export const editServiceProvider = (
  jointBillServiceId: IOverviewJointBillService["jointBillServiceId"],
  jointBillServiceAgreementId: IBillData["jointBillServiceAgreementId"],
  data: IEditServiceProviderRequest,
): Promise<string> =>
  api
    .put<string>(
      `/joint-bill-service-agreements/${jointBillServiceAgreementId}`,
      data,
      { headers: { jointBillServiceId } },
    )
    .then((resp) => resp.data);

export const editServiceProviderDueDay = (
  data: IEditServiceProviderDueDayRequest,
): Promise<string> =>
  api
    .put<string>(
      `/joint-bill-service-agreements/${data.jointBillServiceAgreementId}/due-day`,
      data,
      { headers: { jointBillServiceId: data.jointBillServiceId } },
    )
    .then((resp) => resp.data);

export const removeServiceProvider = (
  jointBillServiceId: IOverviewJointBillService["jointBillServiceId"],
  jointBillServiceAgreementId: IBillData["jointBillServiceAgreementId"],
): Promise<string> =>
  api
    .delete<string>(
      `/joint-bill-service-agreements/${jointBillServiceAgreementId}`,
      { headers: { jointBillServiceId } },
    )
    .then((resp) => resp.data);

export const transferServiceProvider = (data: {
  fromJointBillServiceId: IOverviewJointBillService["jointBillServiceId"];
  toJointBillServiceId: IOverviewJointBillService["jointBillServiceId"];
  jointBillServiceAgreementId: IBillData["jointBillServiceAgreementId"];
}) =>
  api.put(
    `joint-bill-service-agreements/${data.jointBillServiceAgreementId}/transfer`,
    {
      jointBillServiceId: data.toJointBillServiceId,
    },
    { headers: { jointBillServiceId: data.fromJointBillServiceId } },
  );

export const removeRequestedSp = (
  requestedServiceProviderBillId: IRequestedSpBillData["requestedServiceProviderBillId"],
): Promise<string> =>
  api
    .delete<string>(
      `/requested-service-provider-bills/${requestedServiceProviderBillId}`,
    )
    .then((resp) => resp.data);

export const restoreServiceProvider = (
  jointBillServiceId: IOverviewJointBillService["jointBillServiceId"],
  jointBillServiceAgreementId: IBillData["jointBillServiceAgreementId"],
) =>
  api.put(
    `/joint-bill-service-agreements/${jointBillServiceAgreementId}/restore`,
    undefined,
    { headers: { jointBillServiceId } },
  );

export const getRequestedServiceProviders = async () =>
  (
    await api.get<{ requestedServiceProviders: IRequestedServiceProvider[] }>(
      "/requested-providers",
    )
  ).data?.requestedServiceProviders;

export const addRequestedServiceProvider = ({
  jointBillServiceId,
  data,
}: {
  jointBillServiceId: IOverviewJointBillService["jointBillServiceId"];
  data: IAddRequestedServiceProviderData;
}) =>
  api
    .post<IAddServiceProviderResponse>(`/requested-providers`, data, {
      headers: {
        jointBillServiceId,
      },
    })
    .then((resp) => resp.data);

export const getRequestedSpBillFile = (
  requestedServiceProviderBillId: IRequestedSpBillData["requestedServiceProviderBillId"],
) =>
  api
    .get<IBase64File>(
      `/requested-service-provider-bills/${requestedServiceProviderBillId}/files`,
    )
    .then((resp) => resp.data);

export const getServiceProviderPaymentInstructionCodes = async (
  data: IServiceProviderPaymentInstructionCodeRequest,
) =>
  (
    await api.get<IServiceProviderPaymentInstructionCodeReponse>(
      "/bills/payment-instructions/service-provider-payment-instruction-codes",
      {
        params: {
          paymentCode: data.paymentCode,
          serviceProviderId: data.serviceProviderId,
        },
      },
    )
  ).data;
